import React, { memo, useEffect, useMemo } from 'react';

import produce from 'immer';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useQueryState } from 'hooks/useQueryState';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useVersionStore } from 'store/version.store';

import { useQueryGetResourceListForChart } from 'api/queries/getResourceListForChart.query';

import { IState } from 'components/common/GlobalFilterComponent/GlobalFilterComponent';
import classes from 'components/common/GlobalFilterComponent/GlobalFilterComponent.module.scss';
import { FilterLabels } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { Select } from 'components/DesignSystem/Select';

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    setState?: React.Dispatch<React.SetStateAction<IState>>;
    handleFilters?: () => void;
    overrideLatestVersionId?: number | undefined;
    skipFormSet?: boolean;
}

export const ResourceFilter = memo(
    ({ methods, name, setState, handleFilters, overrideLatestVersionId, skipFormSet = false }: Props) => {
        const { version: versionFromStore } = useVersionStore();
        const [queryStringValue] = useQueryState('resource');
        const { getValueFromStore } = useGlobalFilterDataStore();
        const storeValue = getValueFromStore<number>('resource');

        const latestVersionId = overrideLatestVersionId ?? versionFromStore?.id;

        const { data, isFetching } = useQueryGetResourceListForChart({
            latestVersion: latestVersionId,
        });

        const options = useMemo<ISelectOption<number>[]>(() => (data ? data : []), [data]);

        const formValue = methods?.getValues(name);
        const isValue = formValue || queryStringValue || storeValue;

        useEffect(() => {
            if (setState) {
                if (!isValue) {
                    setState((prev) => {
                        return produce(prev, (draft) => {
                            draft.resource.isReady = true;
                            draft.resource.isEmpty = true;
                            return draft;
                        });
                    });
                }
                if (isValue && !skipFormSet) {
                    methods?.setValue(name, Number(isValue));
                    setState((prev) => {
                        return produce(prev, (draft) => {
                            draft.resource.isReady = true;
                            return draft;
                        });
                    });
                }
            }
        }, [isValue, data, setState]);

        const handleChange = (value: ISelectOption<number> | null) => {
            methods?.setValue(name, value ? value.value : null);
            handleFilters && handleFilters();
        };

        const handleValue = (value) => {
            if (!value) {
                return null;
            }
            return data?.find((item) => item.value === Number(value)) || null;
        };

        return (
            <div className={classes.selectWrapper}>
                <div className={classes.activityCodesSelectWrapper}>
                    <Controller
                        render={({ field }) => (
                            <Select<ISelectOption<number>>
                                {...field}
                                options={options}
                                id={'global-filter-component-resource'}
                                isClearable={true}
                                isLoading={isFetching}
                                onChange={handleChange}
                                value={handleValue(field.value)}
                                placeholder={'Select resource...'}
                                label={FilterLabels.resource}
                            />
                        )}
                        name={name}
                        control={methods?.control}
                    />
                </div>
            </div>
        );
    },
);
